import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { publicLandingPreview } from '../data/publicLandingPreview';
import PublicNavbar from './PublicNavbar';
import '../css/PublicLanding.css';
import { apiFetch } from '../api';

import usePageTitle from '../hooks/usePageTitle';
function hasValidToken() {
  const token = localStorage.getItem('authToken');
  if (!token) return false;

  try {
    const { exp } = jwtDecode(token);
    return exp * 1000 >= Date.now();
  } catch {
    return false;
  }
}

export default function PublicLanding() {
  usePageTitle('');
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState('');
  const [demoSent, setDemoSent] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState('');
  const [antispamToken, setAntispamToken] = useState('');
  const formStartedAtRef = useRef(Date.now());

  useEffect(() => {
    const attachToken = async () => {
      try {
        if (window.NeurotrainingAntispam && typeof window.NeurotrainingAntispam.getToken === 'function') {
          const token = await window.NeurotrainingAntispam.getToken();
          if (token) {
            setAntispamToken(String(token));
          }
        }
      } catch {
        // Keep the form usable when antispam provider is not configured.
      }
    };

    attachToken();
  }, []);

  useEffect(() => {
    const sectionIds = ['hero', 'anteprima', 'servizi', 'contatti-card', 'licenza-card'];
    const sectionElements = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (!sectionElements.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (!visibleEntries.length) return;
        const visibleId = visibleEntries[0].target.id;
        // Sull'hero non c'e una voce di menu da evidenziare.
        setActiveSection(visibleId === 'hero' ? '' : visibleId);
      },
      {
        root: null,
        rootMargin: '-100px 0px -45% 0px',
        threshold: [0.2, 0.35, 0.6],
      }
    );

    sectionElements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  const keepPublicView = searchParams.get('license') === '1';

  const {
    progressDelta,
    averageSessionTime,
    averageErrorDelta,
    progressBars,
    recentSessions,
    kpis,
  } = publicLandingPreview;

  if (hasValidToken() && !keepPublicView) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleDemoSubmit = async (event) => {
    event.preventDefault();
    setDemoError('');
    setDemoSent(false);
    setDemoLoading(true);

    try {
      const form = event.currentTarget;
      const formData = new FormData(form);
      const payload = {
        fullName: String(formData.get('fullName') || '').trim(),
        email: String(formData.get('email') || '').trim(),
        organization: String(formData.get('organization') || '').trim(),
        message: String(formData.get('message') || '').trim(),
        website: String(formData.get('website') || '').trim(),
        formStartedAt: Number(formData.get('formStartedAt')),
        antispamToken: String(formData.get('antispamToken') || '').trim()
      };

      const response = await apiFetch('/api/public/demo-request', {
        auth: false,
        method: 'POST',
        body: payload
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        setDemoError(result.message || 'Invio non riuscito. Riprova.');
        return;
      }

      setDemoSent(true);
      form.reset();
      formStartedAtRef.current = Date.now();
    } catch {
      setDemoError('Errore di connessione. Riprova tra poco.');
    } finally {
      setDemoLoading(false);
    }
  };

  return (
    <div className="public-landing-page">
      <PublicNavbar activeLink={activeSection} />

      <main className="public-landing-main">
        {/* HERO SECTION */}
        <section id="hero" className="public-hero-section public-section-band public-section-hero-band">
          <div className="public-hero-content">
            <p className="public-kicker">Piattaforma Neurotraining</p>
            <h1 className="public-title">Allenamento cognitivo guidato, misurabile e condivisibile.</h1>
            <p className="public-subtitle">
              Supportiamo professionisti e centri nel creare percorsi personalizzati, monitorare progressi reali
              e coinvolgere pazienti con esperienze digitali accessibili.
            </p>
          </div>
          <div className="public-hero-visual">
            <svg viewBox="0 0 300 280" className="public-hero-icon" aria-hidden="true">
              <defs>
                <linearGradient id="heroGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1e6fd9" />
                  <stop offset="100%" stopColor="#0f4fa5" />
                </linearGradient>
                <linearGradient id="heroBrainCore" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#7cb7ff" />
                  <stop offset="100%" stopColor="#1e6fd9" />
                </linearGradient>
              </defs>
              {/* Halo di sfondo */}
              <circle cx="150" cy="128" r="92" fill="url(#heroGrad)" opacity="0.12" />
              {/* Forma esterna cervello */}
              <path
                d="M 82 152 C 70 126, 76 96, 98 84 C 104 64, 122 50, 144 54 C 161 42, 183 44, 198 58 C 223 58, 242 79, 241 103 C 255 118, 258 143, 247 163 C 247 189, 226 210, 198 212 C 183 226, 161 228, 144 216 C 120 220, 97 208, 87 188 C 70 179, 65 164, 82 152 Z"
                fill="url(#heroBrainCore)"
                opacity="0.95"
              />
              {/* Emisferi */}
              <path d="M 149 66 C 136 78, 135 96, 145 109 C 127 120, 124 140, 136 154 C 122 165, 122 184, 137 196" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="4" strokeLinecap="round" />
              <path d="M 152 66 C 164 78, 166 96, 156 109 C 174 121, 177 140, 164 155 C 177 166, 178 184, 163 196" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="4" strokeLinecap="round" />
              {/* Solchi principali */}
              <path d="M 103 102 C 117 92, 131 92, 142 102" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="3.5" strokeLinecap="round" />
              <path d="M 160 102 C 170 92, 186 92, 201 102" fill="none" stroke="rgba(255,255,255,0.65)" strokeWidth="3.5" strokeLinecap="round" />
              <path d="M 98 140 C 118 128, 136 128, 146 140" fill="none" stroke="rgba(255,255,255,0.58)" strokeWidth="3.5" strokeLinecap="round" />
              <path d="M 154 140 C 164 128, 182 128, 202 140" fill="none" stroke="rgba(255,255,255,0.58)" strokeWidth="3.5" strokeLinecap="round" />
              <path d="M 111 176 C 124 168, 136 168, 144 176" fill="none" stroke="rgba(255,255,255,0.56)" strokeWidth="3.5" strokeLinecap="round" />
              <path d="M 156 176 C 166 168, 178 168, 191 176" fill="none" stroke="rgba(255,255,255,0.56)" strokeWidth="3.5" strokeLinecap="round" />
              {/* Nodi e connessioni */}
              <path d="M 62 210 Q 112 186 151 192 Q 192 198 240 176" fill="none" stroke="#16a34a" strokeWidth="4.5" strokeLinecap="round" opacity="0.9" />
              <circle cx="88" cy="198" r="6" fill="#16a34a" />
              <circle cx="149" cy="192" r="6" fill="#16a34a" />
              <circle cx="210" cy="191" r="6" fill="#16a34a" />
              {/* Bagliore */}
              <circle cx="134" cy="120" r="24" fill="#ffffff" opacity="0.14" />
            </svg>
          </div>
        </section>

        <section id="anteprima" className="public-feature-preview public-section-band public-section-preview-band">
          <div className="public-feature-heading">
            <p className="public-kicker">Anteprima delle funzionalità</p>
            <h2 className="public-feature-title">Output demo realistici, subito leggibili</h2>
            <p className="public-feature-subtitle">
              Una preview della dashboard operativa: andamento pazienti, storico sessioni e metriche sintetiche,
              pronta per essere mostrata già dal primo incontro.
            </p>
          </div>

          <div className="public-feature-grid">
            <article className="public-feature-card public-feature-card-chart">
              <div className="public-feature-card-head">
                <h3>Progressi ultimi 14 giorni</h3>
                <span className="public-feature-pill">{progressDelta}</span>
              </div>
              <div className="public-mini-chart" role="img" aria-label="Grafico progressi demo">
                {progressBars.map((height, index) => (
                  <span key={`progress-${index}`} style={{ height: `${height}%` }} />
                ))}
              </div>
              <p className="public-feature-note">Tempo medio sessione: <strong>{averageSessionTime}</strong> - Errori medi: <strong>{averageErrorDelta}</strong></p>
            </article>

            <article className="public-feature-card public-feature-card-table">
              <div className="public-feature-card-head">
                <h3>Sessioni recenti</h3>
                <span className="public-feature-pill public-feature-pill-soft">Demo</span>
              </div>
              <ul className="public-feature-list">
                {recentSessions.map((session, index) => (
                  <li key={`session-${index}`}>
                    <span>{session.patient}</span>
                    <strong>{session.activity}</strong>
                    <em>{session.score}</em>
                  </li>
                ))}
              </ul>
            </article>

            <article className="public-feature-card public-feature-card-kpi">
              <div className="public-feature-card-head">
                <h3>KPI operativi</h3>
              </div>
              <div className="public-kpi-grid">
                {kpis.map((kpi, index) => (
                  <div key={`kpi-${index}`}>
                    <span>{kpi.label}</span>
                    <strong>{kpi.value}</strong>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </section>

        {/* 3 SERVICE CARDS */}
        <section className="public-services-hero public-section-band public-section-services-band" id="servizi">
          <article className="public-service-hero-card">
            <div className="public-service-icon">
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <defs>
                  <linearGradient id="stat-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1e6fd9" />
                    <stop offset="100%" stopColor="#14a085" />
                  </linearGradient>
                </defs>
                <circle cx="60" cy="60" r="34" fill="url(#stat-grad)" opacity="0.18" />
                <path d="M 28 82 L 28 54" stroke="url(#stat-grad)" strokeWidth="8" strokeLinecap="round" />
                <path d="M 48 82 L 48 40" stroke="url(#stat-grad)" strokeWidth="8" strokeLinecap="round" opacity="0.85" />
                <path d="M 68 82 L 68 58" stroke="url(#stat-grad)" strokeWidth="8" strokeLinecap="round" opacity="0.72" />
                <path d="M 88 82 L 88 32" stroke="url(#stat-grad)" strokeWidth="8" strokeLinecap="round" opacity="0.92" />
                <path d="M 28 82 Q 44 52 58 58 T 88 32" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </div>
            <h3>Dashboard Statistiche</h3>
            <p>
              Visualizza in tempo reale i progressi dei pazienti con grafici intuitivi, metriche chiave
              e comparazioni storiche per una visione completa dei risultati.
            </p>
          </article>

          <article className="public-service-hero-card">
            <div className="public-service-icon">
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <defs>
                  <linearGradient id="patient-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#0f4fa5" />
                    <stop offset="100%" stopColor="#16a34a" />
                  </linearGradient>
                </defs>
                {/* Persona */}
                <circle cx="60" cy="35" r="18" fill="url(#patient-grad)" />
                <path d="M 30 60 Q 30 50 60 50 Q 90 50 90 60 L 90 85 Q 90 95 60 95 Q 30 95 30 85 Z" fill="url(#patient-grad)" opacity="0.85" />
                {/* Dati attorno */}
                <rect x="20" y="15" width="25" height="2" fill="#14a085" />
                <rect x="75" y="20" width="20" height="2" fill="#14a085" />
                <rect x="10" y="75" width="18" height="2" fill="#14a085" />
                <rect x="92" y="70" width="18" height="2" fill="#14a085" />
              </svg>
            </div>
            <h3>Gestione Pazienti</h3>
            <p>
              Organizza e monitora facilmente tutti i tuoi pazienti con dati strutturati, cronologie
              di sessioni e configurazioni personalizzate per ogni percorso terapeutico.
            </p>
          </article>

          <article className="public-service-hero-card">
            <div className="public-service-icon">
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <defs>
                  <linearGradient id="game-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1e6fd9" />
                    <stop offset="100%" stopColor="#0f4fa5" />
                  </linearGradient>
                </defs>
                {/* Puzzle/Game controller */}
                <rect x="25" y="30" width="70" height="60" rx="8" fill="url(#game-grad)" opacity="0.85" />
                {/* Pezzi puzzle stilizzati */}
                <circle cx="40" cy="45" r="8" fill="#ffffff" opacity="0.6" />
                <circle cx="60" cy="45" r="8" fill="#ffffff" opacity="0.6" />
                <circle cx="80" cy="45" r="8" fill="#ffffff" opacity="0.6" />
                <circle cx="40" cy="65" r="8" fill="#ffffff" opacity="0.5" />
                <circle cx="80" cy="65" r="8" fill="#ffffff" opacity="0.5" />
                <circle cx="60" cy="75" r="8" fill="#16a34a" opacity="0.8" />
                <path d="M 42 98 Q 60 84 78 98" fill="none" stroke="#16a34a" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </div>
            <h3>Giochi Cognitivi</h3>
            <p>
              Batterie strutturate di esercizi per memoria, attenzione e funzioni esecutive con
              difficoltà progressiva, timing regolabile e feedback immediato.
            </p>
          </article>
        </section>

        <section className="public-proof public-section-band public-section-proof-band">
          <article className="public-proof-box public-proof-box-audience">
            <div className="public-proof-icon" aria-hidden="true">
              <svg viewBox="0 0 120 120">
                <circle cx="60" cy="34" r="16" fill="#1e6fd9" opacity="0.9" />
                <circle cx="34" cy="52" r="12" fill="#14a085" opacity="0.85" />
                <circle cx="86" cy="52" r="12" fill="#0f4fa5" opacity="0.8" />
                <path d="M 18 92 Q 18 70 40 70 L 80 70 Q 102 70 102 92" fill="none" stroke="#1e6fd9" strokeWidth="8" strokeLinecap="round" />
              </svg>
            </div>
            <div className="public-proof-content">
              <p className="public-proof-kicker">Per chi è pensata</p>
              <h3>Per team clinici che vogliono operare subito</h3>
              <ul className="public-proof-list">
                <li>Centri di riabilitazione e studi specialistici</li>
                <li>Terapisti e trainer cognitivi con percorsi ricorrenti</li>
                <li>Strutture che vogliono dati leggibili senza complessità tecnica</li>
              </ul>
            </div>
          </article>
          <article className="public-proof-box public-proof-box-value">
            <div className="public-proof-icon" aria-hidden="true">
              <svg viewBox="0 0 120 120">
                <rect x="22" y="22" width="76" height="76" rx="22" fill="#0f4fa5" opacity="0.12" />
                <path d="M 36 68 L 52 84 L 84 42" fill="none" stroke="#16a34a" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="84" cy="42" r="10" fill="#1e6fd9" opacity="0.85" />
              </svg>
            </div>
            <div className="public-proof-content">
              <p className="public-proof-kicker">Cosa ottieni</p>
              <h3>Una piattaforma già pronta da presentare e usare</h3>
              <ul className="public-proof-list">
                <li>Accesso immediato con schermate e risultati già valorizzati</li>
                <li>Esperienza giochi completa pensata per presentazioni e test guidati</li>
                <li>Statistiche e viste operative utili da mostrare fin dal primo incontro</li>
              </ul>
            </div>
          </article>
        </section>

        <section id="licenza-card" className="public-license-section public-section-band public-section-contact-band public-anchor-card">
          <div className="public-license-heading">
            <p className="public-kicker">Richiedi licenza</p>
            <h2>Parliamone e attiviamo il tuo team</h2>
            <p>Contattaci per una proposta su misura: onboarding, configurazione iniziale e supporto operativo inclusi.</p>
          </div>

          <div id="contatti" className="public-contact-grid">
            <article id="contatti-card" className="public-contact-card public-anchor-card">
              <h3>Contattaci</h3>
              <p>Parla con il team Neurotraining per conoscere modalità di adozione e casi d'uso nel tuo contesto.</p>
              <ul className="public-contact-list">
                <li>Email: support@neurotraining.it</li>
                <li>Orari: Lun-Ven 9:00-18:00</li>
              </ul>
              <div className="public-contact-highlights">
                <div>
                  <span>Tempo medio risposta</span>
                  <strong>entro 24h</strong>
                </div>
                <div>
                  <span>Onboarding iniziale</span>
                  <strong>in 7 giorni</strong>
                </div>
              </div>
              <div className="public-contact-actions">
                <a href="mailto:support@neurotraining.it" className="public-secondary-btn">Scrivici ora</a>
                <a href="#licenza-card" className="public-secondary-btn">Prenota una call</a>
              </div>
            </article>

            <article className="public-contact-card">
              <h3>Richiedi licenza</h3>
              <p>Se la prova ti convince, lasciaci i riferimenti: ti ricontattiamo per attivazione, onboarding e proposta licenza.</p>
              <form className="public-demo-form" onSubmit={handleDemoSubmit}>
                <input type="text" name="fullName" placeholder="Nome e cognome" required />
                <input type="email" name="email" placeholder="Email professionale" required />
                <input type="text" name="organization" placeholder="Struttura / Centro" required />
                <textarea name="message" rows="3" placeholder="Esigenze, numero utenti o richieste di attivazione" />
                <input type="hidden" name="formStartedAt" value={formStartedAtRef.current} />
                <input type="hidden" name="antispamToken" value={antispamToken} readOnly />
                <label className="public-honeypot" aria-hidden="true">
                  Lascia vuoto questo campo
                  <input type="text" name="website" tabIndex="-1" autoComplete="off" />
                </label>
                <button type="submit" className="public-primary-btn" disabled={demoLoading}>
                  {demoLoading ? 'Invio in corso...' : 'Richiedi licenza'}
                </button>
              </form>
              {demoError && <p className="public-demo-error">{demoError}</p>}
              {demoSent && <p className="public-demo-success">Richiesta inviata. Ti contatteremo a breve per la licenza.</p>}
            </article>
          </div>
        </section>

        <section className="public-site-footer-links">
          <div className="public-footer-links-grid">
            <div>
              <h4>Neurotraining</h4>
              <a href="#anteprima">Prodotto</a>
              <a href="#servizi">Funzionalità</a>
              <Link to="/pricing">Piani &amp; Prezzi</Link>
              <a href="/login?demo=1">Prova demo</a>
            </div>
            <div>
              <h4>Azienda</h4>
              <a href="#contatti-card">Contact us</a>
              <a href="#licenza-card">Sales</a>
              <a href="#">Chi siamo</a>
            </div>
            <div>
              <h4>Risorse</h4>
              <a href="#">Supporto</a>
              <a href="#">Documentazione</a>
              <a href="#">Status</a>
            </div>
            <div>
              <h4>Legale</h4>
              <Link to="/imprint">Note legali</Link>
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Termini</Link>
            </div>
          </div>
          <p className="public-footer-note">&copy; {new Date().getFullYear()} Neurotraining. All rights reserved.</p>
        </section>
      </main>
    </div>
  );
}
